import { useCallback } from 'react';
import { useMemo } from 'react';
import { MyOrganization } from 'libs/end-to-end/src/my-organization';
import { MyUser } from 'libs/end-to-end/src/my-user';
import { AnalyticsEvent } from 'libs/end-to-end/src/analytics/AnalyticsEvent';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { analyticsTrackRoute } from 'libs/end-to-end/src/api-routing';
import { analyticsIdentifyRoute } from 'libs/end-to-end/src/api-routing';

let distinctId: string | undefined = undefined;
let superProperties: Record<string, unknown> = {};

export interface UseAnalyticsProviderPayload {
  trackEvent: (event: AnalyticsEvent) => void;
  login: (user: MyUser) => void;
  registerOrganization: (organization: MyOrganization) => void;
  registerDatabase: ({
    databaseId,
    databaseName,
    databaseRevisionId,
  }: {
    databaseId: string;
    databaseName: string;
    databaseRevisionId: string;
  }) => void;
  logout: () => void;
}

const useMixpanelAnalytics = (): UseAnalyticsProviderPayload => {
  const trackEvent = useCallback((event: AnalyticsEvent) => {
    const apiBaseUrl = getApiBaseUrl();
    const url = `${apiBaseUrl}/${analyticsTrackRoute}`;

    const payload = {
      eventName: event.eventName,
      eventProperties: event.eventProperties,
      distinctId: distinctId,
      superProperties: superProperties,
    };

    // Fire and forget - don't wait for response
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      credentials: 'include',
    }).catch((error) => {
      // Silently fail - we don't want analytics to break the app
      console.debug('Analytics tracking failed:', error);
    });
  }, []);

  const login = useCallback((user: MyUser) => {
    distinctId = user.id;

    const apiBaseUrl = getApiBaseUrl();
    const url = `${apiBaseUrl}/${analyticsIdentifyRoute}`;

    // Fire and forget - don't wait for response
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        distinctId: user.id,
        properties: {
          $email: user.email,
          'Is Super Admin': user.roleAssignments === 'superAdmin',
        },
      }),
      credentials: 'include',
    }).catch((error) => {
      console.debug('Analytics identify failed:', error);
    });
  }, []);

  const logout = useCallback(() => {
    distinctId = undefined;
    superProperties = {};
  }, []);

  const registerOrganization = useCallback((organization: MyOrganization) => {
    superProperties = {
      ...superProperties,
      'Selected Organization Id': organization.id,
      'Selected Organization Name': organization.name,
    };
  }, []);

  const registerDatabase = useCallback(
    ({
      databaseId,
      databaseName,
      databaseRevisionId,
    }: {
      databaseId: string;
      databaseName: string;
      databaseRevisionId: string;
    }) => {
      superProperties = {
        ...superProperties,
        'Selected Database Id': databaseId,
        'Selected Database Name': databaseName,
        'Selected Database Revision Id': databaseRevisionId,
      };
    },
    [],
  );

  return useMemo(() => {
    return {
      trackEvent,
      registerOrganization,
      registerDatabase,
      login,
      logout,
    };
  }, [login, logout, registerDatabase, registerOrganization, trackEvent]);
};

export interface UseAnalyticsPayload {
  trackEvent: (event: AnalyticsEvent) => void;
  login: (user: MyUser) => void;
  logout: () => void;
  registerOrganization: (organization: MyOrganization) => void;
  registerDatabase: ({
    databaseId,
    databaseName,
    databaseRevisionId,
  }: {
    databaseId: string;
    databaseName: string;
    databaseRevisionId: string;
  }) => void;
}
export const useAnalytics = (): UseAnalyticsPayload => {
  const mixpanel = useMixpanelAnalytics();

  const trackEvent = useCallback(
    (event: AnalyticsEvent) => {
      mixpanel.trackEvent(event);
    },
    [mixpanel],
  );

  const login = useCallback(
    (user: MyUser) => {
      mixpanel.login(user);
    },
    [mixpanel],
  );

  const logout = useCallback(() => {
    mixpanel.logout();
  }, [mixpanel]);

  const registerOrganization = useCallback(
    (organization: MyOrganization) => {
      mixpanel.registerOrganization(organization);
    },
    [mixpanel],
  );

  const registerDatabase = useCallback(
    ({
      databaseId,
      databaseName,
      databaseRevisionId,
    }: {
      databaseId: string;
      databaseName: string;
      databaseRevisionId: string;
    }) => {
      mixpanel.registerDatabase({
        databaseId,
        databaseName,
        databaseRevisionId,
      });
    },
    [mixpanel],
  );

  return useMemo(() => {
    return {
      trackEvent,
      login,
      logout,
      registerOrganization,
      registerDatabase,
    };
  }, [login, logout, registerDatabase, registerOrganization, trackEvent]);
};
